import { useState, useMemo, useEffect } from 'react';

function getDiasDelMes(anio, mes) {
  const primerDia = new Date(anio, mes, 1);
  const ultimoDia = new Date(anio, mes + 1, 0);
  const dias = [];
  let inicioSemana = primerDia.getDay();
  inicioSemana = inicioSemana === 0 ? 6 : inicioSemana - 1;
  for (let i = 0; i < inicioSemana; i++) dias.push(null);
  for (let d = 1; d <= ultimoDia.getDate(); d++) dias.push(d);
  return dias;
}

function formatFecha(anio, mes, dia) {
  const m = String(mes + 1).padStart(2, '0');
  const d = String(dia).padStart(2, '0');
  return `${anio}-${m}-${d}`;
}

function generarSlots(apertura, cierre) {
  if (!apertura || !cierre) return [];
  const slots = [];
  let [h] = apertura.split(':').map(Number);
  const [hCierre] = cierre.split(':').map(Number);
  while (h < hCierre) {
    slots.push(String(h).padStart(2, '0') + ':00');
    h++;
  }
  return slots;
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEMANA = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

export default function CalendarioCancha({ cancha, recinto, duracion = 1, onSeleccion }) {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes,  setMes]  = useState(hoy.getMonth());
  const [diaSeleccionado,  setDiaSeleccionado]  = useState(null);
  const [horaSeleccionada, setHoraSeleccionada] = useState(null);
  const [ocupados, setOcupados] = useState([]);

  const horariosRecinto = useMemo(() =>
    generarSlots(recinto?.apertura, recinto?.cierre),
    [recinto]
  );

  // Cargar ocupados desde BD al cambiar mes/cancha
  useEffect(() => {
    if (!cancha?.id) return;
    fetch(`${process.env.REACT_APP_API_URL}/api/canchas/${cancha.id}/disponibilidad?mes=${mes + 1}&anio=${anio}`)
      .then(r => r.json())
      .then(data => { if (data.ocupados) setOcupados(data.ocupados); })
      .catch(() => {});
  }, [cancha?.id, mes, anio]);

  const dias = useMemo(() => getDiasDelMes(anio, mes), [anio, mes]);

  const esPasado = (dia) => {
    if (!dia) return false;
    const fecha = new Date(anio, mes, dia);
    const hoyInicio = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return fecha < hoyInicio;
  };

  const estaOcupado = (dia, hora) => {
    if (!dia) return false;
    const fecha = formatFecha(anio, mes, dia);
    const horaNum = parseInt(hora.split(':')[0]);
    const slots = [];
    for (let i = 0; i < duracion; i++) {
      const h = horaNum + i;
      slots.push(`${fecha}:${String(h).padStart(2,'0')}:00`);
    }
    return slots.some(s => ocupados.includes(s));
  };

  const tieneBloqueLibre = (dia) => {
    if (!dia || esPasado(dia)) return false;
    if (horariosRecinto.length === 0) return true;
    return horariosRecinto.some(h => !estaOcupado(dia, h));
  };

  const mesAnterior = () => {
    if (mes === 0) { setMes(11); setAnio(anio - 1); }
    else setMes(mes - 1);
    setDiaSeleccionado(null);
    setHoraSeleccionada(null);
  };

  const mesSiguiente = () => {
    if (mes === 11) { setMes(0); setAnio(anio + 1); }
    else setMes(mes + 1);
    setDiaSeleccionado(null);
    setHoraSeleccionada(null);
  };

  const seleccionarDia = (dia) => {
    if (!dia || esPasado(dia)) return;
    setDiaSeleccionado(dia);
    setHoraSeleccionada(null);
  };

  const seleccionarHora = (hora) => {
    if (estaOcupado(diaSeleccionado, hora)) return;
    setHoraSeleccionada(hora);
    const fecha = formatFecha(anio, mes, diaSeleccionado);
    onSeleccion({ fecha, hora, cancha });
  };

  const getHoraFin = (horaInicio) => {
    const h = parseInt(horaInicio) + duracion;
    return String(h).padStart(2, '0') + ':00';
  };

  const horariosParaMostrar = horariosRecinto.length > 0
    ? horariosRecinto
    : ['08:00','09:00','10:00','11:00','12:00','15:00','16:00','17:00','18:00','19:00','20:00'];

  return (
    <div style={styles.wrap}>

      {/* Header cancha */}
      <div style={styles.canchaHeader}>
        <div style={styles.canchaIcono}>🏟️</div>
        <div>
          <div style={styles.canchaNombre}>{cancha?.nombre}</div>
          <div style={styles.canchaInfo}>
            · ${Number(cancha?.precio)?.toLocaleString('es-CL')}/hr
            {cancha?.techada ? ' · Indoor' : ' · Outdoor'}
          </div>
        </div>
      </div>

      {/* Navegación mes */}
      <div style={styles.navMes}>
        <button style={styles.navBtn} onClick={mesAnterior}>←</button>
        <div style={styles.mesActual}>{MESES[mes]} {anio}</div>
        <button style={styles.navBtn} onClick={mesSiguiente}>→</button>
      </div>

      {/* Cabecera días semana */}
      <div style={styles.cabeceraSemana}>
        {DIAS_SEMANA.map(d => (
          <div key={d} style={styles.cabeceraDia}>{d}</div>
        ))}
      </div>

      {/* Grilla días */}
      <div style={styles.grillaDias}>
        {dias.map((dia, i) => {
          const pasado    = esPasado(dia);
          const libre     = tieneBloqueLibre(dia);
          const seleccion = diaSeleccionado === dia;
          return (
            <div
              key={i}
              onClick={() => seleccionarDia(dia)}
              style={{
                ...styles.diaCell,
                background: !dia        ? 'transparent'
                          : seleccion   ? '#1A6BFF'
                          : pasado      ? '#F8FAFC'
                          : libre       ? 'rgba(22,163,74,0.12)'
                          : '#F4F6FA',
                color: !dia        ? 'transparent'
                     : seleccion   ? 'white'
                     : pasado      ? '#CBD5E1'
                     : libre       ? '#16A34A'
                     : '#9BA5B7',
                cursor: dia && !pasado ? 'pointer' : 'default',
                border: seleccion
                  ? '1.5px solid #1A6BFF'
                  : dia && libre
                  ? '1.5px solid rgba(22,163,74,0.3)'
                  : '1.5px solid transparent',
              }}
            >
              {dia || ''}
            </div>
          );
        })}
      </div>

      {/* Leyenda */}
      <div style={styles.leyenda}>
        <div style={styles.leyendaItem}>
          <div style={{ ...styles.leyendaDot, background: '#16A34A' }} />
          <span>Disponible</span>
        </div>
        <div style={styles.leyendaItem}>
          <div style={{ ...styles.leyendaDot, background: '#F4F6FA', border: '1px solid #E8ECF4' }} />
          <span>Sin disponibilidad</span>
        </div>
        <div style={styles.leyendaItem}>
          <div style={{ ...styles.leyendaDot, background: '#F8FAFC', border: '1px solid #E8ECF4' }} />
          <span>Fecha pasada</span>
        </div>
      </div>

      {/* Bloques horarios */}
      {diaSeleccionado && (
        <div style={styles.horariosWrap}>
          <div style={styles.horariosTitle}>
            Horarios disponibles — {diaSeleccionado} de {MESES[mes]}
          </div>
          <div style={styles.horariosGrid}>
            {horariosParaMostrar.map(hora => {
              const ocupado   = estaOcupado(diaSeleccionado, hora);
              const seleccion = horaSeleccionada === hora;
              return (
                <div
                  key={hora}
                  onClick={() => !ocupado && seleccionarHora(hora)}
                  style={{
                    ...styles.horaBloque,
                    background: ocupado   ? '#F4F6FA'
                              : seleccion ? '#1A6BFF'
                              : 'rgba(22,163,74,0.1)',
                    border: `1.5px solid ${
                      ocupado   ? '#E8ECF4'
                    : seleccion ? '#1A6BFF'
                    : 'rgba(22,163,74,0.4)'
                    }`,
                    color: ocupado   ? '#CBD5E1'
                         : seleccion ? 'white'
                         : '#16A34A',
                    cursor: ocupado ? 'not-allowed' : 'pointer',
                  }}
                >
                  <div style={styles.horaNum}>{hora}</div>
                  <div style={styles.horaFin}>
                    {ocupado ? 'Ocupado' : `hasta ${getHoraFin(hora)}`}
                  </div>
                  <div style={styles.horaEstado}>
                    {ocupado   ? '✗'
                   : seleccion ? '✓ Elegido'
                   : 'Libre'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Confirmación */}
      {diaSeleccionado && horaSeleccionada && (
        <div style={styles.seleccionConfirmada}>
          <div style={styles.seleccionIcono}>✓</div>
          <div style={styles.seleccionTexto}>
            <strong>{diaSeleccionado} de {MESES[mes]} {anio}</strong>
            {' · '}
            <strong>{horaSeleccionada} — {getHoraFin(horaSeleccionada)}</strong>
            {' · '}
            {cancha?.nombre}
            {' · '}
            <strong style={{ color: '#1A6BFF' }}>
              ${(Number(cancha?.precio) * duracion)?.toLocaleString('es-CL')}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { width: '100%' },
  canchaHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: '#F4F6FA', border: '1px solid #E8ECF4',
    borderRadius: 12, padding: '12px 16px', marginBottom: 16,
  },
  canchaIcono: { fontSize: 24, flexShrink: 0 },
  canchaNombre: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 16, fontWeight: 700, color: '#141922', marginBottom: 2,
  },
  canchaInfo: {
    fontSize: 12, color: '#6B7280',
    fontFamily: "'Barlow', sans-serif",
  },
  navMes: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: 12,
  },
  navBtn: {
    background: '#F4F6FA', border: '1px solid #E8ECF4',
    borderRadius: 8, width: 32, height: 32,
    color: '#141922', fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  mesActual: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 18, fontWeight: 700, color: '#141922',
  },
  cabeceraSemana: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4, marginBottom: 6,
  },
  cabeceraDia: {
    fontSize: 10, fontWeight: 700, color: '#9BA5B7',
    textAlign: 'center', textTransform: 'uppercase',
    letterSpacing: '0.04em', fontFamily: "'Barlow', sans-serif",
    padding: '4px 0',
  },
  grillaDias: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 4, marginBottom: 12,
  },
  diaCell: {
    aspectRatio: '1',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 8, fontSize: 13, fontWeight: 600,
    fontFamily: "'Barlow', sans-serif", transition: 'all 0.12s',
  },
  leyenda: {
    display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap',
  },
  leyendaItem: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, color: '#9BA5B7',
    fontFamily: "'Barlow', sans-serif",
  },
  leyendaDot: { width: 10, height: 10, borderRadius: '50%' },
  horariosWrap: {
    background: '#F4F6FA', border: '1px solid #E8ECF4',
    borderRadius: 12, padding: '14px 16px', marginBottom: 12,
  },
  horariosTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 14, fontWeight: 700, color: '#141922', marginBottom: 12,
  },
  horariosGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
  },
  horaBloque: {
    borderRadius: 10, padding: '10px 8px',
    textAlign: 'center', transition: 'all 0.12s',
  },
  horaNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 16, fontWeight: 700, marginBottom: 2,
  },
  horaFin: {
    fontSize: 9, fontFamily: "'Barlow', sans-serif",
    opacity: 0.7, marginBottom: 2,
  },
  horaEstado: {
    fontSize: 9, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    fontFamily: "'Barlow', sans-serif",
  },
  seleccionConfirmada: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'rgba(26,107,255,0.08)',
    border: '1.5px solid rgba(26,107,255,0.25)',
    borderRadius: 10, padding: '10px 14px',
  },
  seleccionIcono: { color: '#1A6BFF', fontSize: 16, fontWeight: 700 },
  seleccionTexto: {
    fontSize: 13, color: '#141922',
    fontFamily: "'Barlow', sans-serif",
  },
};